import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { PipelineProvider } from "@/lib/pipelineContext.jsx";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Pipeline from "@/pages/Pipeline";
import DealDetail from "@/pages/DealDetail";
import Gates from "@/pages/Gates";
import GateEditor from "@/pages/GateEditor";
import Layout, { defaultLandingForRole } from "@/components/Layout";
import Superadmin from "@/pages/Superadmin";
import InboxPage from "@/pages/Inbox";
import AgentLibrary from "@/pages/AgentLibrary";
import AgentEditor from "@/pages/AgentEditor";
import UserManagement from "@/pages/UserManagement";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Tasks from "@/pages/Tasks";
import NewDealApprovals from "@/pages/NewDealApprovals";
import FieldMapping from "@/pages/FieldMapping";
import GateRunReview from "@/pages/GateRunReview";
import CustomReports from "@/pages/CustomReports";
import AutoGates from "@/pages/AutoGates";
import Profile from "@/pages/Profile";
import BulkOps from "@/pages/BulkOps";
import BulkRemap from "@/pages/BulkRemap";
import PipelineDashboard from "@/pages/reports/PipelineDashboard";
import DealAging from "@/pages/reports/DealAging";
import AgentAnalytics from "@/pages/reports/AgentAnalytics";
import AiTokenUsage from "@/pages/reports/AiTokenUsage";
import EmailReports from "@/pages/reports/EmailReports";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import TenantWorkspace from "@/pages/TenantWorkspace";

// Route → roles allowed (spec 0.9 route protection). v5.7 Addition 4:
// Coming-soon routes (Portfolio, Fund Management, Monitoring) are removed.
const ROUTE_ROLES = {
  "/pipeline": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/inbox": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/gates": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/agents": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/reports": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/users": ["vc_admin", "it_admin"],
  "/settings": ["vc_admin"],
  "/notifications": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/tasks": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/approvals": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/agent-runs/:runId/map": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/gate-runs/:runId": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/reports/custom": ["vc_admin", "it_admin", "senior_vc"],
  "/reports/email-reports": ["vc_admin", "senior_vc"],
  "/autogates": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/profile": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/bulk": ["vc_admin", "it_admin", "senior_vc", "junior_vc"],
  "/bulk-remap": ["vc_admin"],
  "/superadmin": ["superadmin"],
};

function roleAllowedForPath(role, path) {
  // Match longest prefix in ROUTE_ROLES
  const keys = Object.keys(ROUTE_ROLES).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (path === k || path.startsWith(k + "/")) return ROUTE_ROLES[k].includes(role);
  }
  // Deal detail sub-route belongs to tenant users
  if (path.startsWith("/deals/")) {
    return ["vc_admin", "it_admin", "senior_vc", "junior_vc"].includes(role);
  }
  return true;
}

function Protected({ children, superadminOnly, tenantOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    if (superadminOnly && user.role !== "superadmin") return;
    if (tenantOnly && user.role === "superadmin") return;
    if (!roleAllowedForPath(user.role, location.pathname)) {
      toast.error("You do not have access to this section.");
    }
  }, [user, location.pathname, superadminOnly, tenantOnly]);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center text-[var(--cw-muted)]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (superadminOnly && user.role !== "superadmin") return <Navigate to="/pipeline" replace />;
  if (tenantOnly && user.role === "superadmin") return <Navigate to="/superadmin" replace />;
  if (!roleAllowedForPath(user.role, location.pathname)) {
    return <Navigate to={defaultLandingForRole(user.role)} replace />;
  }
  return <Layout>{children}</Layout>;
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultLandingForRole(user.role)} replace />;
}

// Junior VC cannot access Agent Run Analytics (spec H-R3.2)
function AgentAnalyticsGuard() {
  const { user } = useAuth();
  if (user?.role === "junior_vc") {
    return <Navigate to="/reports/pipeline" replace />;
  }
  return <AgentAnalytics />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PipelineProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<RoleHome />} />
              <Route path="/superadmin" element={<Protected superadminOnly><Superadmin /></Protected>} />
              <Route path="/superadmin/tenants/:tenantId" element={<Protected superadminOnly><TenantWorkspace /></Protected>} />
              <Route path="/pipeline" element={<Protected tenantOnly><Pipeline /></Protected>} />
              <Route path="/inbox" element={<Protected tenantOnly><InboxPage /></Protected>} />
              <Route path="/deals/:dealId" element={<Protected tenantOnly><DealDetail /></Protected>} />
              <Route path="/gates" element={<Protected tenantOnly><Gates /></Protected>} />
              <Route path="/gates/new" element={<Protected tenantOnly><GateEditor /></Protected>} />
              <Route path="/gates/:gateId" element={<Protected tenantOnly><GateEditor /></Protected>} />
              <Route path="/agents" element={<Protected tenantOnly><AgentLibrary /></Protected>} />
              <Route path="/agents/new" element={<Protected tenantOnly><AgentEditor /></Protected>} />
              <Route path="/agents/:agentId" element={<Protected tenantOnly><AgentEditor /></Protected>} />
              <Route path="/users" element={<Protected tenantOnly><UserManagement /></Protected>} />
              <Route path="/notifications" element={<Protected tenantOnly><Notifications /></Protected>} />
              <Route path="/settings" element={<Protected tenantOnly><Settings /></Protected>} />
              <Route path="/tasks" element={<Protected tenantOnly><Tasks /></Protected>} />
              <Route path="/approvals" element={<Protected tenantOnly><NewDealApprovals /></Protected>} />
              <Route path="/agent-runs/:runId/map" element={<Protected tenantOnly><FieldMapping /></Protected>} />
              <Route path="/gate-runs/:runId" element={<Protected tenantOnly><GateRunReview /></Protected>} />
              <Route path="/reports/custom" element={<Protected tenantOnly><CustomReports /></Protected>} />
              <Route path="/autogates" element={<Protected tenantOnly><AutoGates /></Protected>} />
              <Route path="/profile" element={<Protected tenantOnly><Profile /></Protected>} />
              <Route path="/bulk" element={<Protected tenantOnly><BulkOps /></Protected>} />
              <Route path="/bulk-remap" element={<Protected tenantOnly><BulkRemap /></Protected>} />
              <Route path="/reports" element={<Navigate to="/reports/pipeline" replace />} />
              <Route path="/reports/pipeline" element={<Protected tenantOnly><PipelineDashboard /></Protected>} />
              <Route path="/reports/aging" element={<Protected tenantOnly><DealAging /></Protected>} />
              <Route path="/reports/agents" element={<Protected tenantOnly><AgentAnalyticsGuard /></Protected>} />
              <Route path="/reports/ai-usage" element={<Protected tenantOnly><AiTokenUsage /></Protected>} />
              <Route path="/reports/email-reports" element={<Protected tenantOnly><EmailReports /></Protected>} />
            </Routes>
          </BrowserRouter>
        </PipelineProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
